import { Component } from '@theme/component';
import { loadGoogleMaps } from '@theme/google-maps';

const EARTH_RADIUS_KM = 6371;
// Falls back to the same default as the block's `radius_km` setting, for merchants on a theme
// version predating that setting (block.settings.radius_km blank -> data-radius-km="").
const DEFAULT_SAME_DAY_DELIVERY_RADIUS_KM = 18;

const DELIVERY_DATE_FORMATTER = new Intl.DateTimeFormat('en-AU', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/**
 * Great-circle distance between two lat/lng points, in kilometres. Duplicated from
 * assets/find-in-store.js (not imported) since that file doesn't export it and this widget has
 * no other dependency on find-in-store.js.
 * @param {{ lat: number, lng: number }} a - The first point.
 * @param {{ lat: number, lng: number }} b - The second point.
 * @returns {number} The distance between the two points, in kilometres.
 */
function haversineDistanceKm(a, b) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

/**
 * STUB — placeholder eligibility check, grounded in this product's real current stock locations
 * rather than an arbitrary rule: same day delivery is treated as available whenever at least one
 * store currently stocking the selected variant is within `radiusKm` of the shopper's selected
 * location. Replace this function's body with a real courier/dispatch API call when one exists;
 * #checkEligibility only depends on the Promise<boolean> contract, so nothing else needs to change.
 * @param {{ lat: number, lng: number }} location - The shopper's selected place.
 * @param {{ handle: string, name: string, lat: number, lng: number }[]} stockingStores - Stores
 *   currently stocking this variant (parsed from the JSON island this block renders, built the
 *   same way snippets/store-availability-list.liquid does for "Find in store").
 * @param {number} radiusKm - Merchant-configured same day delivery radius (block setting `radius_km`).
 * @returns {Promise<boolean>} Resolves true if same day delivery is available at this location.
 */
async function checkSameDayDeliveryEligibility(location, stockingStores, radiusKm) {
  // Simulate a network round-trip so the `checking` loading state is visibly exercised.
  await new Promise((resolve) => setTimeout(resolve, 600));
  return stockingStores.some((store) => haversineDistanceKm(store, location) <= radiusKm);
}

/**
 * Computes the delivery date shown in the eligible message: today if checked before the cutoff
 * hour on a weekday, otherwise the next weekday (Saturdays/Sundays are skipped).
 * @param {number} cutoffHour - The local hour (0-23) after which same day dispatch is no longer
 *   possible for today.
 * @returns {string} A formatted date, e.g. "Thursday 23 July 2026".
 */
function computeDeliveryDate(cutoffHour) {
  const now = new Date();
  const isWeekday = now.getDay() >= 1 && now.getDay() <= 5;
  const beforeCutoff = now.getHours() < cutoffHour;

  const date = new Date(now);
  if (!(isWeekday && beforeCutoff)) {
    date.setDate(date.getDate() + 1);
    while (date.getDay() === 0 || date.getDay() === 6) {
      date.setDate(date.getDate() + 1);
    }
  }

  return DELIVERY_DATE_FORMATTER.format(date);
}

/**
 * @typedef {object} Refs
 * @property {HTMLElement} header - The icon + label row that also hosts the desktop hover/focus tooltip.
 * @property {HTMLElement} text - The always-visible status label.
 * @property {HTMLButtonElement} [tooltipTrigger] - Opens the tooltip on tap (mobile only; conditionally rendered).
 * @property {HTMLButtonElement} [tooltipClose] - Closes the tooltip on tap (mobile only; conditionally rendered).
 * @property {HTMLButtonElement} triggerButton - Reveals the search form from the idle state.
 * @property {HTMLInputElement} input - The suburb/postcode search input.
 * @property {HTMLButtonElement} checkButton - Runs the eligibility check.
 * @property {HTMLUListElement} suggestions - The Places Autocomplete suggestions listbox.
 * @property {HTMLElement} suggestionsStatus - Visually-hidden live region announcing suggestion counts.
 * @property {HTMLButtonElement} resetButton - "Check another location" — returns to the search form.
 * @property {HTMLButtonElement} retryButton - The invalid-state link back to the search form.
 * @property {HTMLScriptElement} dataScript - JSON island of stores currently stocking this variant.
 */

/**
 * The "Same day delivery" PDP widget: an informational tooltip (CSS-only hover/focus on
 * desktop; click-to-open with an explicit close button on touch devices, see toggleTooltip/
 * closeTooltip below) alongside an interactive postcode/suburb eligibility checker (Google
 * Places Autocomplete + the stub eligibility rule above).
 * @extends {Component<Refs>}
 */
class SameDayDelivery extends Component {
  requiredRefs = [
    'header',
    'text',
    'triggerButton',
    'input',
    'checkButton',
    'suggestions',
    'suggestionsStatus',
    'resetButton',
    'retryButton',
    'dataScript',
  ];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #debounceTimer;

  /** @type {any} */
  #sessionToken;

  /** @type {{ prediction: any, label: string }[]} */
  #suggestions = [];

  /** @type {number} The keyboard-highlighted suggestion, or -1 for none. */
  #activeIndex = -1;

  /** @type {Promise<void> | undefined} Lazily created on first search so Maps never loads on page load. */
  #mapsReady;

  /** @type {number} Bumped per request so a stale, out-of-order response can't overwrite a newer one. */
  #suggestionsRequestId = 0;

  /** @type {number} Bumped per eligibility check so a stale resolution can't overwrite a newer one. */
  #checkRequestId = 0;

  /** @type {{ suburb: string, state: string, postcode: string, lat: number, lng: number, label: string } | null} */
  #selectedPlace = null;

  /** @type {{ handle: string, name: string, lat: number, lng: number }[]} */
  #stockingStores = [];

  connectedCallback() {
    super.connectedCallback();
    this.#stockingStores = this.#parseStockingStores();
    this.#wireSearch();
    this.#wireTooltipDisclosure();
    document.addEventListener('keydown', this.#handleGlobalKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#handleGlobalKeydown);
  }

  /**
   * Parses this block's own rendered JSON island of stores currently stocking the selected
   * variant, mirroring find-in-store.js's `dataScript.textContent` parsing.
   * @returns {{ handle: string, name: string, lat: number, lng: number }[]}
   */
  #parseStockingStores() {
    try {
      return JSON.parse(this.refs.dataScript.textContent ?? '[]');
    } catch (error) {
      console.warn('[same-day-delivery] Could not parse store data.', error);
      return [];
    }
  }

  /**
   * Opens the informational tooltip on tap — desktop (hover-capable) devices rely purely on
   * the CSS :hover/:focus-within reveal instead, so this is a no-op there.
   */
  toggleTooltip() {
    if (window.matchMedia('(hover: hover)').matches) return;

    if (this.refs.header.hasAttribute('data-open')) {
      this.closeTooltip();
    } else {
      this.refs.header.setAttribute('data-open', '');
      this.refs.tooltipTrigger?.setAttribute('aria-expanded', 'true');
    }
  }

  /** Closes the tap-opened tooltip (see toggleTooltip) — the mobile close button's handler. */
  closeTooltip() {
    this.refs.header.removeAttribute('data-open');
    this.refs.tooltipTrigger?.setAttribute('aria-expanded', 'false');
    this.refs.tooltipTrigger?.focus();
  }

  /**
   * Escape dismisses the tooltip regardless of how it was opened — the mobile tap-opened state
   * (closes it directly), or the desktop hover/focus-within reveal (which has no JS-tracked open
   * state, so it's suppressed via `data-dismissed` instead — see the matching CSS in the
   * `@media (hover: hover)` block). Listens on `document` since a mouse-hovering user may have no
   * focus inside the widget at all. Required per WCAG 1.4.13 (Dismissable): a hover/focus-revealed
   * tooltip that obscures other content must be closable without moving the pointer or focus.
   * @param {KeyboardEvent} event
   */
  #handleGlobalKeydown = (event) => {
    if (event.key !== 'Escape') return;

    if (this.refs.header.hasAttribute('data-open')) {
      this.closeTooltip();
      return;
    }

    if (this.refs.header.matches(':hover') || this.refs.header.contains(document.activeElement)) {
      this.refs.header.setAttribute('data-dismissed', '');
    }
  };

  /**
   * Keeps the desktop hover/focus-within tooltip's dismissed state and the trigger's
   * `aria-expanded` in sync with hover/focus, since that reveal is pure CSS with no other JS
   * involved. Wired unconditionally (a no-op when there's no tooltip_content, since
   * tooltipTrigger/the tip itself won't exist) — always checks live `matchMedia` rather than
   * caching it, so it stays correct if hover capability changes (e.g. a hybrid touch/mouse
   * device).
   */
  #wireTooltipDisclosure() {
    const header = this.refs.header;

    const clearDismissed = () => {
      if (!header.matches(':hover') && !header.contains(document.activeElement)) {
        header.removeAttribute('data-dismissed');
      }
    };

    const syncExpanded = () => {
      if (!window.matchMedia('(hover: hover)').matches) return;
      const isOpen = header.matches(':hover') || header.contains(document.activeElement);
      this.refs.tooltipTrigger?.setAttribute('aria-expanded', String(isOpen));
    };

    header.addEventListener('mouseleave', clearDismissed);
    header.addEventListener('focusout', clearDismissed);
    header.addEventListener('mouseenter', syncExpanded);
    header.addEventListener('mouseleave', syncExpanded);
    header.addEventListener('focusin', syncExpanded);
    header.addEventListener('focusout', syncExpanded);
  }

  /**
   * Reveals the postcode/suburb search form, replacing the idle trigger. The header label
   * swaps from the intro copy to the same "Check for same day delivery" wording as the trigger
   * it replaces — it stays visible (not hidden) for the rest of the widget's states.
   */
  expand() {
    this.dataset.state = 'expanded';
    this.refs.triggerButton.setAttribute('aria-expanded', 'true');
    this.#setHeaderLabel(this.refs.triggerButton.textContent.trim());
    this.refs.input.focus();
  }

  /**
   * Clears the current selection/input and returns to the search form — used by both the
   * ineligible/eligible "Check another location" button and the invalid-state retry link.
   */
  reset() {
    this.#selectedPlace = null;
    this.refs.input.value = '';
    this.dataset.state = 'expanded';
    this.refs.triggerButton.setAttribute('aria-expanded', 'true');
    this.#setHeaderLabel(this.refs.triggerButton.textContent.trim());
    this.refs.input.focus();
  }

  /**
   * Every status/result message (checking, invalid, eligible, ineligible) replaces the header
   * label directly rather than appearing in a separate block below it — the header is the
   * widget's one always-visible line.
   * @param {string} text
   */
  #setHeaderLabel(text) {
    this.refs.text.textContent = text;
  }

  /**
   * Same as #setHeaderLabel, but each `__TOKEN__` placeholder's replacement is wrapped in
   * <strong> (e.g. the ineligible message's suburb/state) — built as DOM nodes rather than an
   * HTML string, so neither the merchant's configured template nor the place data can be
   * misinterpreted as markup.
   * @param {string} template - Message template containing `__TOKEN__` placeholders.
   * @param {Record<string, string>} values - Token (e.g. "__SUBURB__") -> replacement text.
   */
  #setHeaderLabelWithEmphasis(template, values) {
    const tokenPattern = new RegExp(Object.keys(values).join('|'), 'g');
    const nodes = [];
    let lastIndex = 0;

    for (const match of template.matchAll(tokenPattern)) {
      const start = match.index ?? 0;
      if (start > lastIndex) nodes.push(document.createTextNode(template.slice(lastIndex, start)));

      const strong = document.createElement('strong');
      strong.textContent = values[match[0]] ?? '';
      nodes.push(strong);

      lastIndex = start + match[0].length;
    }
    if (lastIndex < template.length) nodes.push(document.createTextNode(template.slice(lastIndex)));

    this.refs.text.replaceChildren(...nodes);
  }

  /**
   * Runs the (stubbed) eligibility check for the currently selected place, or falls back to the
   * `invalid` state if no suggestion was ever selected.
   */
  async checkEligibility() {
    if (!this.#selectedPlace) {
      this.dataset.state = 'invalid';
      this.#setHeaderLabel(this.dataset.invalidMessage ?? '');
      this.refs.retryButton.focus();
      return;
    }

    const place = this.#selectedPlace;
    const requestId = ++this.#checkRequestId;

    this.dataset.state = 'checking';
    this.refs.input.disabled = true;
    this.refs.checkButton.disabled = true;
    this.#setHeaderLabel(this.dataset.checkingMessage ?? '');

    try {
      // `Number('')` is 0, not NaN — an explicit blank check (rather than Number.isNaN) is needed
      // to fall back correctly when data-radius-km="" (block.settings.radius_km blank).
      const radiusKm = this.dataset.radiusKm ? Number(this.dataset.radiusKm) : DEFAULT_SAME_DAY_DELIVERY_RADIUS_KM;
      const eligible = await checkSameDayDeliveryEligibility(place, this.#stockingStores, radiusKm);
      if (requestId !== this.#checkRequestId) return;

      this.refs.input.disabled = false;
      this.refs.checkButton.disabled = false;

      if (eligible) {
        const rawCutoffHour = Number(this.dataset.cutoffHour);
        // `|| 12` would silently override a legitimate midnight cutoff (0 is falsy in JS) — only
        // fall back to the default when the setting is genuinely missing/invalid.
        const cutoffHour = Number.isNaN(rawCutoffHour) ? 12 : rawCutoffHour;
        const date = computeDeliveryDate(cutoffHour);
        const template = this.dataset.eligibleMessage ?? '';
        this.#setHeaderLabel(
          template
            .replaceAll('__DATE__', date)
            .replaceAll('__POSTCODE__', place.postcode)
            .replaceAll('__STATE__', place.state)
        );
        this.dataset.state = 'eligible';
      } else {
        const template = this.dataset.ineligibleMessage ?? '';
        this.#setHeaderLabelWithEmphasis(template, { __SUBURB__: place.suburb, __STATE__: place.state });
        this.dataset.state = 'ineligible';
      }

      // input/checkButton (whichever had focus) are hidden in both outcome states — the reset
      // button is what actually stays visible there, so focus needs to land there instead, after
      // the state flip (setting it earlier would just get force-blurred to <body> once the form
      // that contains input/checkButton disappears).
      this.refs.resetButton.focus();
    } catch (error) {
      if (requestId !== this.#checkRequestId) return;
      console.warn('[same-day-delivery] Eligibility check failed.', error);
      this.refs.input.disabled = false;
      this.refs.checkButton.disabled = false;
      this.#setHeaderLabel(this.dataset.errorMessage ?? '');
      this.dataset.state = 'invalid';
      this.refs.retryButton.focus();
    }
  }

  /**
   * Parses a Google Place's address components into the fields the eligible/ineligible messages
   * need. Mirrors the shape (not the code) of find-in-store.js's own place-resolution step.
   * @param {any} place - A resolved `google.maps.places.Place`.
   * @returns {{ suburb: string, state: string, postcode: string }}
   */
  #parseAddressComponents(place) {
    const components = place.addressComponents ?? [];
    const find = (type) => components.find((component) => component.types?.includes(type));

    return {
      suburb: find('locality')?.longText ?? '',
      state: find('administrative_area_level_1')?.longText ?? '',
      postcode: find('postal_code')?.longText ?? '',
    };
  }

  /**
   * Wires up the suburb/postcode search: Google Places Autocomplete, keyboard navigation, and
   * ARIA combobox/listbox semantics — ported in shape from `assets/find-in-store.js`'s
   * `#wireSearch`, scoped to `this`/`this.refs` instead of a portalled dialog (this widget has
   * no dialog to portal). Degrades to a Maps-unavailable state when no API key is configured or
   * the Maps script fails to load.
   */
  #wireSearch() {
    const apiKey = this.dataset.mapsApiKey;
    if (!apiKey) {
      this.setAttribute('data-maps-unavailable', '');
      return;
    }

    const ensureMapsReady = () => {
      this.#mapsReady ??= loadGoogleMaps(apiKey, { libraries: ['places'] }).catch((error) => {
        console.warn('[same-day-delivery] Failed to load Google Maps.', error);
        this.setAttribute('data-maps-unavailable', '');
        // Unlike the no-API-key path (caught synchronously before anything is shown), this can
        // fire after the shopper has already expanded the form — explain why it just disappeared.
        this.#setHeaderLabel(this.dataset.searchUnavailable ?? '');
        // `[data-maps-unavailable]` hides the form (and trigger/retry/result/reset) entirely, so a
        // focused input/checkButton would otherwise get force-blurred to <body>. tooltipTrigger is
        // the only element guaranteed to stay visible/focusable here (when tooltip_content is
        // configured) — nothing else in the widget remains interactive in this degraded state.
        this.refs.tooltipTrigger?.focus();
      });
      return this.#mapsReady;
    };

    const announce = (message) => {
      this.refs.suggestionsStatus.textContent = message ?? '';
    };

    const clearSuggestions = () => {
      clearTimeout(this.#debounceTimer);
      this.#suggestions = [];
      this.#activeIndex = -1;
      this.refs.suggestions.replaceChildren();
      this.refs.suggestions.hidden = true;
      this.refs.input.setAttribute('aria-expanded', 'false');
      this.refs.input.removeAttribute('aria-activedescendant');
    };

    const renderSuggestions = () => {
      if (!this.#suggestions.length) {
        clearSuggestions();
        announce(this.dataset.suggestionsNone);
        return;
      }

      const listbox = this.refs.suggestions;
      listbox.replaceChildren(
        ...this.#suggestions.map(({ label }, index) => {
          const option = document.createElement('li');
          option.id = `${listbox.id}-option-${index}`;
          option.className = 'same-day-delivery__suggestion';
          option.setAttribute('role', 'option');
          option.setAttribute('aria-selected', 'false');
          option.textContent = label;
          return option;
        })
      );
      listbox.hidden = false;
      this.refs.input.setAttribute('aria-expanded', 'true');
      this.#activeIndex = -1;
      this.refs.input.removeAttribute('aria-activedescendant');
      const template = this.dataset.suggestionsAvailableTemplate ?? '';
      announce(template.replace('__COUNT__', String(this.#suggestions.length)));
    };

    /**
     * Moves the keyboard highlight through the suggestions, wrapping at both ends.
     * @param {number} delta - `1` for the next suggestion, `-1` for the previous.
     */
    const moveActiveSuggestion = (delta) => {
      const count = this.#suggestions.length;
      const index = this.#activeIndex === -1 ? (delta > 0 ? 0 : count - 1) : (this.#activeIndex + delta + count) % count;

      const options = this.refs.suggestions.querySelectorAll('[role="option"]');
      options.forEach((option, i) => {
        const isActive = i === index;
        option.classList.toggle('same-day-delivery__suggestion--active', isActive);
        option.setAttribute('aria-selected', String(isActive));
      });

      this.#activeIndex = index;
      this.refs.input.setAttribute('aria-activedescendant', options[index]?.id ?? '');
      options[index]?.scrollIntoView({ block: 'nearest' });
    };

    const requestSuggestions = async (query) => {
      await ensureMapsReady();
      const AutocompleteSuggestion = window.google?.maps?.places?.AutocompleteSuggestion;
      if (!AutocompleteSuggestion) return;

      this.#sessionToken ??= new google.maps.places.AutocompleteSessionToken();
      const requestId = ++this.#suggestionsRequestId;

      try {
        const { suggestions: results } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,
          sessionToken: this.#sessionToken,
          includedRegionCodes: ['au'],
          includedPrimaryTypes: ['locality', 'postal_code'],
        });

        // Ignore responses that arrive out of order behind a newer request.
        if (requestId !== this.#suggestionsRequestId) return;

        this.#suggestions = (results ?? [])
          .map((suggestion) => suggestion.placePrediction)
          .filter(Boolean)
          .map((prediction) => ({ prediction, label: prediction.text?.text ?? '' }));
        renderSuggestions();
      } catch (error) {
        if (requestId !== this.#suggestionsRequestId) return;
        console.warn('[same-day-delivery] Autocomplete request failed.', error);
        clearSuggestions();
        announce(this.dataset.searchUnavailable);
      }
    };

    const selectSuggestion = async (index) => {
      const entry = this.#suggestions[index];
      if (!entry) return;

      this.refs.input.value = entry.label;
      clearSuggestions();

      try {
        const place = entry.prediction.toPlace();
        await place.fetchFields({ fields: ['addressComponents', 'location'] });
        if (!place.location) return;

        this.#selectedPlace = {
          ...this.#parseAddressComponents(place),
          lat: place.location.lat(),
          lng: place.location.lng(),
          label: entry.label,
        };
      } catch (error) {
        console.warn('[same-day-delivery] Could not resolve the selected place.', error);
      } finally {
        this.#sessionToken = undefined;
      }

      // Selecting a suggestion is the shopper's confirmation of their location — submit
      // straight away rather than requiring a separate click on "Check".
      if (this.#selectedPlace) this.checkEligibility();
    };

    this.addEventListener('input', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input !== this.refs.input) return;

      // Free-typing after a selection invalidates it — Check must fail until a suggestion is
      // selected again, matching the reference site's behaviour.
      this.#selectedPlace = null;

      const query = input.value.trim();
      clearTimeout(this.#debounceTimer);
      if (!query) {
        clearSuggestions();
        return;
      }
      this.#debounceTimer = setTimeout(() => requestSuggestions(query), 250);
    });

    this.addEventListener('focusout', (event) => {
      if (event.target === this.refs.input) clearSuggestions();
    });

    this.addEventListener('keydown', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input !== this.refs.input) return;
      if (!this.#suggestions.length) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          moveActiveSuggestion(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          moveActiveSuggestion(-1);
          break;
        case 'Enter':
          // With nothing highlighted, Enter picks the top suggestion.
          event.preventDefault();
          selectSuggestion(this.#activeIndex >= 0 ? this.#activeIndex : 0);
          break;
        case 'Escape':
          // Prevent the browser's native search-input clear; just close the list, keep the text.
          event.preventDefault();
          event.stopImmediatePropagation();
          clearSuggestions();
          break;
        default:
          break;
      }
    });

    this.addEventListener('mousedown', (event) => {
      if (/** @type {HTMLElement} */ (event.target).closest('.same-day-delivery__suggestions')) event.preventDefault();
    });

    this.addEventListener('click', (event) => {
      const option = /** @type {HTMLElement} */ (event.target).closest('[role="option"]');
      if (!option || !this.refs.suggestions.contains(option)) return;

      const index = [...this.refs.suggestions.children].indexOf(option);
      if (index >= 0) selectSuggestion(index);
    });
  }
}

if (!customElements.get('same-day-delivery')) {
  customElements.define('same-day-delivery', SameDayDelivery);
}
